import React, { useMemo } from 'react';
import classes from 'new/components/settings/VendorBuilder/VendorBuilder.module.scss';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { Stack } from 'new/elements/Stack';
import {
  dateFormats,
  iterationTypes,
  requestModes,
  responseFormats,
  responseFormatsCSV,
  timeUnits,
} from 'new/components/settings/VendorBuilder/selectTypes';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import { DynamicConfigValues } from 'new/components/settings/VendorBuilder/DynamicConfigValues';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import Typography from 'new/elements/Typography';

interface SourceIterationProps {
  endpointIndex: number;
  index: number;
}

const iterationFields = (endpointIndex, index) => [
  {
    label: (
      <>
        Method <code>source_template["rest.iterations"][{index}].method</code>
      </>
    ),
    ype: 'select',
    items: requestModes,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    required: true,
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}].method`,
    defaultValue: requestModes[0].value,
  },
  {
    label: (
      <>
        API Endpoint <code>source_template["rest.iterations"][{index}]["url.template"]</code>
      </>
    ),
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          API Endpoint <code>source_template["rest.iterations"][{index}]["url.template"]</code>
        </Typography>
        <Typography variant="body2">
          Set the URL from which you wish to fetch data. You can use Nexla backend{' '}
          <code>URL Macros</code> in the API URL.
        </Typography>
      </Stack>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    required: true,
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["url.template"]`,
  },
  {
    label: (
      <>
        Request Body <code>source_template["rest.iterations"][{index}]["body.template"]</code>
      </>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          comparison: 'noteq',
          value: 'data.map.key.queue',
        },
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["method"]`]: [
        'POST',
        'PUT',
      ],
    },
    description: (
      <Typography padding="16px">
        <strong>
          Request Body <code>source_template["rest.iterations"][{index}]["body.template"]</code>
        </strong>
        <br />
        Enter the payload that should be applied when making this request. <br />
        <br />
        <strong>Note:</strong>
        <ol>
          <li>
            Enter a <strong>valid JSON object</strong>
          </li>
          <li>As in the URL, you can also use Nexla macros here.</li>
        </ol>
      </Typography>
    ),
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["body.template"]`,
  },
  {
    label: (
      <>
        (Optional) Request Headers{' '}
        <code>source_template["rest.iterations"][{index}]["request.headers"]</code>
      </>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    description: (
      <Typography padding="16px">
        <strong>
          Optional Request Headers <code>request.headers</code>
        </strong>
        <br />
        Add any optional request headers that must be sent as part of this request. You do not need
        to repeat headers already present in the credentials. Please input the headers as
        comma-separated values, e.g.,
        <code>header1:value1,header2:value2</code>
      </Typography>
    ),
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["request.headers"]`,
  },
  {
    label: (
      <>
        API Response Format{' '}
        <code>source_template["rest.iterations"][{index}]["response.format"]</code>
      </>
    ),
    type: 'select',
    items: responseFormats,
    defaultValue: responseFormats[1].value,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.format"]`,
  },
  {
    label: (
      <>
        Path to Data in Response{' '}
        <code>source_template["rest.iterations"][{index}]["response.data.path"]</code>
      </>
    ),
    type: 'text',
    description: (
      <Typography padding="16px">
        <strong>
          Path to Data in Response{' '}
          <code>source_template["rest.iterations"][{index}]["response.data.path"]</code>
        </strong>
        You can choose which part of the API response should be treated as relevant data by Nexla.
        For example, when pulling a list of items from a REST API endpoint, the API will usually
        return an array of records along with some metadata. You can configure this field such that
        Nexla treats each element of that array as a record.
        <br />
        <br />
        For a JSON API response, please specify the JSON Path that points to the object or array
        that you want Nexla to consider as relevant data, and for an XML API, specify the same as an
        XPATH.
        <br />
        <br />
        For example, the JSONPath could be <code>$.data[*]</code> if the response has an array named
        data at the top level. Select one of the smart tokens generated when you tested the API, or
        try out JSON Path examples at [https://jsonpath.com](https://jsonpath.com).
        <br />
      </Typography>
    ),
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.data.path"]`,
  },
  {
    label: (
      <>
        Path to metadata in Response{' '}
        <code>
          source_template["rest.iterations"][{index}
          ]["response.data.path.additional"]
        </code>
      </>
    ),
    type: 'text',
    description: (
      <Typography padding="16px">
        <strong>
          Path to metadata in Response{' '}
          <code>
            source_template["rest.iterations"][{index}
            ]["response.data.path.additional"]
          </code>
        </strong>
        <br />
        By choosing Path to Data in Response above, you defined which part of the API response
        should be treated as a record by Nexla. However, sometimes, you may also wish to include API
        response data outside the indicated path to data in each record. Set this property if you
        wish to include this common metadata in each record.
        <br />
        <br />
        For example, when pulling a list of items from a REST API endpoint, APIs will usually return
        an array of records along with some metadata. Set the Path to Data in Response such that
        each entry of the array is treated as a valid record. Then, configure this field such that
        the metadata is also included in each record.
        <br /> <br />
        For a JSON API response, please specify the JSON Path that points to the object or array
        that you want Nexla to consider as relevant data, and for an XML API, specify the same as an
        XPATH. Try out JSON Path examples at [https://jsonpath.com](https://jsonpath.com)
      </Typography>
    ),
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        {
          value: 'data.map.key.queue',
          comparison: 'noteq',
        },
    },
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.data.path.additional"]`,
  },

  {
    label: (
      <>
        Date Format for Date/Time Macro{' '}
        <code>source_template["rest.iterations"][{index}]["date.format"]</code>
      </>
    ),
    type: 'select',
    items: dateFormats,
    description:
      'Datetime format to which the output of Nexla datetime macros like {now} will be converted.',
    defaultValue: dateFormats[0].value,
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["date.format"]`,
  },
  {
    label: (
      <>
        Time Unit for Operations{' '}
        <code>source_template["rest.iterations"][{index}]["date.time.unit"]</code>
      </>
    ),
    type: 'select',
    description:
      'The date or time unit used to perform mathematical operations on Nexla datetime macros',
    defaultValue: timeUnits[2].value,
    items: timeUnits,
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["date.time.unit"]`,
  },
  {
    label: (
      <>
        API Iteration / Pagination Type{' '}
        <code>source_template["rest.iterations"][{index}]["iteration.type"]</code>
      </>
    ),
    type: 'select',
    required: true,
    items: iterationTypes,
    defaultValue: iterationTypes[0].value,
    helperText:
      'Set instructions for how the connector should iterate requests over multiple pages in this step before moving on to the next step',
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          API Iteration / Pagination Type{' '}
          <code>source_template["rest.iterations"][{index}]["iteration.type"]</code>
        </Typography>
        <Typography variant="body2">
          Set instructions for how the connector should iterate requests over multiple pages in this
          step before moving on to the next step.
        </Typography>
      </Stack>
    ),
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`,
  },
  {
    label: (
      <>
        Lookup ID <code>source_template["rest.iterations"][{index}]["map.id"]`</code>
      </>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'data.map.key.queue',
    },
    description: 'Lookup IDs that you need to reference as Nexla macros (JSON)',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["map.id"]`,
  },
  {
    label: (
      <>
        URL Parameter ID/Token/Page{' '}
        <code>source_template["rest.iterations"][{index}]["param.id"]</code>
      </>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['paging.incrementing', 'paging.next.token', 'response.id.number', 'response.id.string'],
    },
    description:
      'URL parameter name used to set the ID/page number/token for this type of iteration.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["param.id"]`,
  },
  {
    label: (
      <>
        Start From Page <code>source_template["rest.iterations"][{index}]["start.page.from"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    defaultValue: 1,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['paging.incrementing'],
    },
    description:
      'Set the starting value for the URL page number parameter. Nexla will automatically iterate through subsequent pages of data until no more data remains to be fetched.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["start.page.from"]`,
  },
  {
    label: (
      <>
        End After Page (Optional){' '}
        <code>source_template["rest.iterations"][{index}]["end.page.to"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['paging.incrementing'],
    },
    description:
      '(Optional): You can configure the platform to stop fetching after receiving a specific page number. Leave this field blank to fetch all relevant data.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["end.page.to"]`,
  },
  {
    label: (
      <>
        URL Param for Items per page{' '}
        <code>source_template["rest.iterations"][{index}]["param.page.size"]</code>
      </>
    ),
    type: 'text',
    inputType: 'string',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['paging.incrementing', 'paging.incrementing.offset'],
    },
    description:
      'Enter the URL parameter name used to set the the number of items that are returned in each page.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["param.page.size"]`,
  },
  {
    label: (
      <>
        Items per page{' '}
        <code>source_template["rest.iterations"][{index}]["page.expected.rows"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['paging.incrementing', 'paging.incrementing.offset'],
    },
    description: 'Set the value for the number of items to be fetched in a page of data',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["page.expected.rows"]`,
  },
  {
    label: (
      <>
        Parallel requests{' '}
        <code>
          source_template["rest.iterations"][{index}
          ]["request.parallelism.count"]
        </code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    defaultValue: 1,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.incrementing',
    },
    description:
      'You can configure multiple requests to be executed in parallel by changing this number.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["request.parallelism.count"]`,
  },
  {
    label: (
      <>
        URL Param For Offset{' '}
        <code>source_template["rest.iterations"][{index}]["param.offset"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    required: true,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.incrementing.offset',
    },
    description: 'Enter the URL parameter name used to set the offset for this type of iteration.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["param.offset"]`,
  },
  {
    label: (
      <>
        Start From Offset{' '}
        <code>source_template["rest.iterations"][{index}]["start.offset.from"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    defaultValue: 0,
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.incrementing.offset',
    },
    description: 'Set the starting offset value that should be used for the first request',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["start.offset.from"]`,
  },
  {
    label: (
      <>
        Stop After Offset <code>source_template["rest.iterations"][{index}]["end.offset.to"]</code>
      </>
    ),
    type: 'text',
    inputType: 'number',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.incrementing.offset',
    },
    description: 'Set the starting offset value that should be used for the first request',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["end.offset.to"]`,
  },
  {
    label: (
      <>
        Path to Next Token{' '}
        <code>
          source_template["rest.iterations"][{index}
          ]["response.next.token.data.path"]
        </code>
      </>
    ),
    type: 'text',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.next.token',
    },
    description:
      'Choose which part of the API response should be treated as a relevant token for the next request. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.next.token.data.path"]`,
  },
  {
    label: (
      <>
        Stop After Token{' '}
        <code>
          source_template["rest.iterations"][{index}
          ]["end.token.to"]
        </code>
      </>
    ),
    type: 'text',
    description:
      'You can configure the platform to stop fetching after receiving a specific token. Leave this field blank to fetch all relevant data.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.next.token',
    },
    helperText:
      'You can configure the platform to stop fetching after receiving a specific token. Leave this field blank to fetch all relevant data.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["end.token.to"]`,
  },
  {
    label: (
      <>
        Path to Next URL{' '}
        <code>
          source_template["rest.iterations"][{index}
          ]["response.next.url.data.path"]
        </code>
      </>
    ),
    type: 'text',
    description:
      'Choose which part of the API response should be treated as relevant URLs for the next request. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.next.url',
    },
    helperText:
      'Choose which part of the API response should be treated as relevant URLs for the next request. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.next.url.data.path"]`,
  },
  {
    label: (
      <>
        Stop after URL <code>source_template["rest.iterations"][{index}]["end.url.to"]</code>
      </>
    ),
    type: 'text',
    description:
      'You can configure the platform to stop fetching after receiving a specific URL. Leave this field blank to fetch all relevant data.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'paging.next.url',
    },
    helperText:
      'You can configure the platform to stop fetching after receiving a specific URL. Leave this field blank to fetch all relevant data.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["end.url.to"]`,
  },
  {
    label: (
      <>
        Response Header containing Next Page URL{' '}
        <code>source_template["rest.iterations"][{index}]["link"]</code>
      </>
    ),
    type: 'text',
    description:
      'Set the name of the response header that contains the next page URL. Often, API developers send this data in the Link header',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'link.header',
    },
    helperText:
      'Set the name of the response header that contains the next page URL. Often, API developers send this data in the Link header.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["link"]`,
  },
  {
    label: (
      <>
        Path to ID in Response{' '}
        <code>source_template["rest.iterations"][{index}]["response.id.field.name"]</code>
      </>
    ),
    type: 'text',
    helperText:
      'Choose which part of the API response should be treated as relevant IDs for the next request. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    description:
      'Choose which part of the API response should be treated as relevant IDs for the next request. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'response.id.number',
    },
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["response.id.field.name"]`,
  },
  {
    label: (
      <>
        Next ID Inclusive?{' '}
        <code>source_template["rest.iterations"][{index}]["param.id.inclusive"]</code>
      </>
    ),
    type: 'checkbox',
    description:
      'Set this to true if the highest ID in the current response should be used as the starting ID for fetching the next page of data.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'response.id.number',
    },
    helperText:
      'Set this to true if the highest ID in the current response should be used as the starting ID for fetching the next page of data.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["param.id.inclusive"]`,
  },
  {
    label: (
      <>
        Start From ID <code>source_template["rest.iterations"][{index}]["start.id.from"]</code>
      </>
    ),
    type: 'text',
    description: 'Set the starting value of ID that should be used for the first request.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['response.id.number', 'response.id.string'],
    },
    helperText: 'Set the starting value of ID that should be used for the first request.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["start.id.from"]`,
  },
  {
    label: (
      <>
        Stop After ID <code>source_template["rest.iterations"][{index}]["end.id.to"]</code>
      </>
    ),
    type: 'text',
    description:
      'You can configure the platform to stop fetching after receiving a specific ID. Leave this f ield blank to fetch all relevant data.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        ['response.id.number', 'response.id.string'],
    },
    helperText:
      'You can configure the platform to stop fetching after receiving a specific ID. Leave this f ield blank to fetch all relevant data.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["end.id.to"]`,
  },
  {
    label: (
      <>
        Path to Job Status information in API response{' '}
        <code>source_template["rest.iterations"][{index}]["async.iteration.path"]</code>
      </>
    ),
    type: 'text',
    required: true,
    description:
      'Choose which part of the API response should be treated as the relevant property for evaluating the status of the asynchronous job. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'async.poll',
    },
    helperText:
      'Choose which part of the API response should be treated as the relevant property for evaluating the status of the asynchronous job. This must be a valid JSON Path (for JSON responses) or XPath (for XML responses).',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["async.iteration.path"]`,
  },
  {
    label: (
      <>
        Job Completion Value{' '}
        <code>source_template["rest.iterations"][{index}]["async.iteration.value"]</code>
      </>
    ),
    type: 'text',
    required: true,
    description:
      'Set the value of Job Status Param that indicates completion of the asynchronous job.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'async.poll',
    },
    helperText:
      'Set the value of Job Status Param that indicates completion of the asynchronous job.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["async.iteration.value"]`,
  },
  {
    label: (
      <>
        Data Format of Response File{' '}
        <code>source_template["rest.iterations"][{index}]["file.response.format"]</code>
      </>
    ),
    type: 'select',
    items: responseFormatsCSV,
    required: true,
    description:
      'When an API endpoint returns a file in the response (or when response body is plain text that should be parsed as a file), the platform can be configured to parse the response content accordingly. Configure the type of parsing that should be applied to generate the Nexset.',
    dependsOn: {
      [`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["iteration.type"]`]:
        'body.as.file',
    },
    helperText:
      'When an API endpoint returns a file in the response (or when response body is plain text that should be parsed as a file), the platform can be configured to parse the response content accordingly. Configure the type of parsing that should be applied to generate the Nexset.',
    name: `endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]["file.response.format"]`,
  },
];

export const SourceIteration = (props: SourceIterationProps) => {
  const { endpointIndex, index } = props;
  const { updateVendor } = useVendorBuilder();

  const onRemove = () => {
    updateVendor((newVendor) => {
      newVendor.endpoints[endpointIndex].source_template['rest.iterations'].splice(index, 1);
    });
  };

  const fields = useMemo(() => iterationFields(endpointIndex, index), [endpointIndex, index]);

  return (
    <Stack className={classes.round_container}>
      <Section
        defaultExpanded
        title={`Endpoint #${endpointIndex + 1}: Iteration #${index + 1}`}
        gap="16px"
      >
        <Stack alignItems="flex-end">
          <IconButton color="error" onClick={onRemove}>
            <DeleteIcon />
          </IconButton>
        </Stack>
        <FormBuilder fields={fields} />
        <DynamicConfigValues
          path={`endpoints[${endpointIndex}].source_template["rest.iterations"][${index}]`}
        />
      </Section>
    </Stack>
  );
};
